import { Link, useLocation } from 'react-router-dom';
import { Button, Skeleton } from '@moneyskyline/ui';
import { AmountText } from '@/components/AmountText';
import { HonestInline } from '@/components/HonestInline';
import { scopeHref } from '@/app/shell/scope';
import { useExportReview } from '@/api/hooks/useExportReview';
import { BusinessNav } from './BusinessNav';
import { RecordsDisclaimer } from './RecordsDisclaimer';

export function ExportPage() {
  const taxYear = new Date().getFullYear();
  const { search } = useLocation();
  const reviewQuery = useExportReview(taxYear);
  const review = reviewQuery.data;

  return (
    <section className="mx-auto w-full max-w-6xl px-4 py-6" aria-labelledby="export-heading">
      <Link to={scopeHref('/business', search)} className="text-sm text-link">‹ Business</Link>
      <h1 id="export-heading" tabIndex={-1} className="text-title font-semibold">Export review</h1>
      <BusinessNav />

      {reviewQuery.isLoading && <Skeleton className="mt-4 h-24 w-full" />}

      {review?.status === 'unavailable' && (
        <div className="mt-4 space-y-4" data-testid="export-unavailable">
          <HonestInline kind="export-unavailable" />
          <div className="rounded-[10px] border border-border bg-card p-4 text-sm text-muted-foreground">
            CSV and PDF run as jobs once the export contract lands. A button that cannot produce a file is disabled rather than faked.
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" disabled title="Export jobs are not available yet">CSV · items</Button>
            <Button type="button" variant="outline" disabled title="Export jobs are not available yet">PDF summary</Button>
          </div>
        </div>
      )}

      {review?.status === 'ready' && (
        <div className="mt-4 grid grid-cols-1 gap-4 desktop:grid-cols-[minmax(0,1fr)_400px]">
          <div className="overflow-hidden rounded-[10px] border border-border bg-card text-sm">
            <div className="bg-degraded-surface px-3 py-2 font-semibold text-degraded-foreground">
              Personal charges on business-use accounts · {review.personal_on_business.length}
            </div>
            {review.personal_on_business.map((item) => (
              <div key={item.line_item_id} className="flex min-h-12 items-center justify-between border-t border-border px-3">
                <span className={item.included ? '' : 'text-muted-foreground line-through'}>
                  {item.merchant} <span className="text-[12px] text-muted-foreground">· {item.date_label} · {item.reason}</span>
                </span>
                <AmountText value={item.amount} currency={review.currency} struck={!item.included} />
              </div>
            ))}
            <div className="bg-degraded-surface px-3 py-2 font-semibold text-degraded-foreground">
              Business charges on personal-use accounts · {review.business_on_personal.length}
            </div>
            {review.business_on_personal.map((item) => (
              <div key={item.line_item_id} className="flex min-h-12 items-center justify-between border-t border-border px-3">
                <span>
                  {item.merchant} <span className="text-[12px] text-muted-foreground">· {item.date_label} · {item.reason}</span>
                </span>
                <AmountText value={item.amount} currency={review.currency} />
              </div>
            ))}
            <div className="flex min-h-12 items-center justify-between border-t border-border px-3">
              <span>Uncategorized business items</span>
              {review.uncategorized_count == null ? (
                <span className="text-muted-foreground">unknown</span>
              ) : (
                <Link to={scopeHref('/transactions/inbox', search)}>{review.uncategorized_count} · fix in Inbox</Link>
              )}
            </div>
            <div className="flex min-h-12 items-center justify-between border-t border-border px-3">
              <span>Missing receipts</span>
              {review.missing_receipt_count == null ? (
                <span className="text-muted-foreground">unknown</span>
              ) : (
                <span>{review.missing_receipt_count}</span>
              )}
            </div>
          </div>
          <div className="rounded-[10px] border border-border bg-card p-5 text-sm">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Packet · Business {review.tax_year}</div>
            <div className="mt-3 grid grid-cols-[1fr_auto] gap-2">
              <span className="text-muted-foreground">Income</span>
              <AmountText value={review.income} currency={review.currency} />
              <span className="text-muted-foreground">Expenses</span>
              <AmountText value={review.expense} currency={review.currency} />
              <span className="text-muted-foreground">Net</span>
              <AmountText value={review.net} currency={review.currency} variant="balance" />
              <span className="text-muted-foreground">Items</span>
              <span className="font-mono">{review.item_count}</span>
              <span className="text-muted-foreground">Events</span>
              <span className="font-mono">{review.event_count}</span>
            </div>
            <p className="mt-3 text-[12px] text-muted-foreground">
              Hide balances masks this screen. Exports show full amounts. Inclusion changes apply to this export request only and do not recategorize the ledger.
            </p>
            <Button type="button" className="mt-4 min-h-11 w-full" disabled>
              Export CSV · items
            </Button>
            <Button type="button" variant="outline" className="mt-2 min-h-11 w-full" disabled>
              Export PDF summary
            </Button>
            <p className="mt-2 text-[12px] text-muted-foreground">Job-backed download is not available yet. Nothing was generated.</p>
          </div>
        </div>
      )}

      <RecordsDisclaimer className="mt-6 max-w-xl" />
    </section>
  );
}

export default ExportPage;
